// deferredBaseline — localStorage helpers for the Phase 3 #15 log-first
// onboarding branch.
//
// When a fresh user picks "Just log for now" at the onboarding branch
// step, we set the marker below and route them to /app instead of
// continuing through baseline + plan-shape. Today renders in deferred-
// baseline mode: FirstRunGuide still shows the three numbered steps,
// but a quick-log chip strip appears inside it so the first tap from a
// fresh account is a dose, not "Continue."
//
// After the user logs their first dose we surface a friendly nudge
// card on Today inviting them to finish baseline + plan-shape. The
// nudge is dismissible per-day via the second key below; if dismissed,
// it re-appears the next calendar day OR once the user has 5+ doses.
//
// Once the user creates a real taper plan (POST /api/taper/plan
// succeeds), OnboardingScreen.finish() clears both keys so the user
// graduates to the normal post-onboarding Today.
//
// All operations are best-effort — quota / privacy mode failures are
// silently swallowed because this is non-load-bearing UX state.

const DEFERRED_KEY = 'qk.deferredBaseline.v1';
const NUDGE_DISMISSED_KEY = 'qk.deferredNudgeDismissed.v1';

export function isDeferredMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DEFERRED_KEY) === '1';
  } catch {
    return false;
  }
}

export function enterDeferredMode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEFERRED_KEY, '1');
  } catch {
    // best-effort
  }
}

export function exitDeferredMode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(DEFERRED_KEY);
    window.localStorage.removeItem(NUDGE_DISMISSED_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Per-day nudge dismissal. The stored value is a YYYY-MM-DD string;
 * the nudge re-appears the next calendar day (or once doseCount >= 5,
 * checked at the call site).
 */
export function isNudgeDismissedToday(todayYmd: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NUDGE_DISMISSED_KEY) === todayYmd;
  } catch {
    return false;
  }
}

export function dismissNudgeForToday(todayYmd: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NUDGE_DISMISSED_KEY, todayYmd);
  } catch {
    // best-effort
  }
}

// In-memory sample plan defaults shown alongside the deferred caption.
// We never write these to D1 — they exist only to give the user a
// concrete target while their first dose lands. Once they finish
// onboarding the real plan overrides everything.
export const SAMPLE_PLAN_DEFAULTS = {
  baselineMg: 50,
  targetTodayMg: 40,
  weeksToZero: 30 / 7, // ~30 days end_date
} as const;
